import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listCreditRates,
  updateCreditRates,
  type CreditFeatureRate,
} from '../api/admin';

export function CreditRatesPage() {
  const queryClient = useQueryClient();
  const ratesQuery = useQuery({
    queryKey: ['admin', 'credit-rates'],
    queryFn: listCreditRates,
  });
  const [draft, setDraft] = useState<Record<string, number>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (ratesQuery.data) {
      setDraft(Object.fromEntries(ratesQuery.data.map((rate) => [rate.feature_key, rate.base_credits])));
    }
  }, [ratesQuery.data]);

  const grouped = useMemo(() => {
    const groups = new Map<string, CreditFeatureRate[]>();
    for (const rate of ratesQuery.data ?? []) {
      groups.set(rate.category, [...(groups.get(rate.category) ?? []), rate]);
    }
    return [...groups.entries()];
  }, [ratesQuery.data]);

  const dirty = (ratesQuery.data ?? []).some(
    (rate) => draft[rate.feature_key] !== rate.base_credits,
  );

  const mutation = useMutation({
    mutationFn: () => updateCreditRates(
      (ratesQuery.data ?? []).map((rate) => ({
        feature_key: rate.feature_key,
        base_credits: draft[rate.feature_key] ?? rate.base_credits,
      })),
    ),
    onSuccess: (rates) => {
      queryClient.setQueryData(['admin', 'credit-rates'], rates);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    },
  });

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Credit Rates</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Set the base credit price for every billable feature. The customer&apos;s plan multiplier is applied after this price.
          </p>
        </div>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!dirty || mutation.isPending}
          className="min-h-11 cursor-pointer rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving…' : 'Save credit rates'}
        </button>
      </div>

      {saved && (
        <div role="status" className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
          Credit rates updated. New actions use these prices immediately.
        </div>
      )}
      {mutation.isError && (
        <div role="alert" className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          Credit rates could not be saved. Please try again.
        </div>
      )}

      {ratesQuery.isLoading && <div className="py-16 text-center text-slate-500">Loading credit rates…</div>}
      {ratesQuery.isError && <div className="py-16 text-center text-red-600">Could not load credit rates.</div>}

      <div className="mt-6 space-y-6">
        {grouped.map(([category, rates]) => (
          <section key={category} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-3">
              <h2 className="text-sm font-bold text-slate-800">{category}</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {rates.map((rate) => (
                <div key={rate.feature_key} className="grid gap-3 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-center">
                  <div>
                    <label htmlFor={`rate-${rate.feature_key}`} className="text-sm font-semibold text-slate-900">
                      {rate.label}
                    </label>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {rate.unit_label} · <span className="font-mono">{rate.feature_key}</span>
                    </p>
                  </div>
                  <div className="relative">
                    <input
                      id={`rate-${rate.feature_key}`}
                      type="number"
                      min={0}
                      max={1_000_000}
                      required
                      value={draft[rate.feature_key] ?? rate.base_credits}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        [rate.feature_key]: Math.max(0, Number.parseInt(event.target.value, 10) || 0),
                      }))}
                      className="min-h-11 w-full rounded-lg border border-slate-300 px-3 pr-16 text-right text-sm font-bold text-slate-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-slate-500">credits</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
